import React, { createContext, useContext } from "react";
import StoreUser from "../../../../types/StoreUser";
import { Community } from "../../../../types/Community";
import {
  useSocialConnections,
  SocialConnections,
} from "../hooks/useSocialConnections";
import {
  useScheduledPosts,
  ScheduledPostsState,
} from "../hooks/useScheduledPosts";
import { useMediaProjects, MediaProjectsState } from "../hooks/useMediaProjects";
import { useMediaConfig, MediaConfigState } from "../hooks/useMediaConfig";

// Studio-wide data: one set of listeners shared by every section instead of
// each tab fetching connections/posts/projects on its own.

export type MediaStudioContextValue = {
  user?: StoreUser;
  community?: Community;
  communityId?: string;
  connections: SocialConnections;
  posts: ScheduledPostsState;
  projects: MediaProjectsState;
  mediaConfig: MediaConfigState;
};

const MediaStudioContext = createContext<MediaStudioContextValue | null>(null);

export const MediaStudioProvider: React.FC<{
  user?: StoreUser;
  community?: Community;
  children: React.ReactNode;
}> = ({ user, community, children }) => {
  const communityId = user?.communityId;
  const connections = useSocialConnections(communityId);
  const posts = useScheduledPosts(communityId);
  const projects = useMediaProjects(communityId);
  const mediaConfig = useMediaConfig(communityId);

  return (
    <MediaStudioContext.Provider
      value={{
        user,
        community,
        communityId,
        connections,
        posts,
        projects,
        mediaConfig,
      }}
    >
      {children}
    </MediaStudioContext.Provider>
  );
};

export const useMediaStudio = (): MediaStudioContextValue => {
  const ctx = useContext(MediaStudioContext);
  if (!ctx) {
    throw new Error("useMediaStudio must be used inside MediaStudioProvider");
  }
  return ctx;
};

export default MediaStudioContext;
